import React, { useState, useMemo, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { MaterialProps, FinishesProps } from '@partsbadger/types';
import { matchesFinishSearch } from '@partsbadger/utils';
import { Button, Input, Spin } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { StepCard } from '../StepCard';
import _ from 'lodash';

interface IProduct {
    finishes?: number[];
    custom_finish?: string;
}
interface IFinishStepProps {
    stepIndex?: number;
    finishes: FinishesProps[];
    finishSelected: { id: number; name: string } | null;
    onNext?: () => void;
    onPrev?: () => void;
    isHobbyist: boolean;
    loading: boolean;
    customFinish: string | null;
    onHandleUpdate: (value: { product: IProduct }) => void;
    onPendingCustomChange?: (stepIndex: number, isPending: boolean) => void;
}

const { Search } = Input;

export function FinishStep({
    stepIndex = 2,
    finishes,
    finishSelected,
    onNext,
    onPrev,
    isHobbyist,
    loading,
    customFinish,
    onHandleUpdate,
    onPendingCustomChange,
}: IFinishStepProps) {
    const [searchQuery, setSearchQuery] = useState('');
    const [pendingCustomFinishId, setPendingCustomFinishId] = useState<number | null>(null);
    const shouldAutoAdvance = useRef(false);

    let data = finishes;

    if (isHobbyist) {
        data = data?.filter(item => {
            return item.name !== 'Review 2D' && item.name !== 'Custom';
        });
    }

    const custom = data.find(item => item.name === 'Custom');

    if (custom) {
        // Exclude custom from list
        data = data.filter(item => item.id !== custom?.id);
    }

    const displayFinishSelected =
        pendingCustomFinishId != null && custom ? { id: custom.id, name: custom.name } : finishSelected;

    useEffect(() => {
        onPendingCustomChange?.(stepIndex, pendingCustomFinishId != null);
    }, [stepIndex, pendingCustomFinishId, onPendingCustomChange]);

    const handleUpdate = (product: IProduct) => {
        const payload = {
            product: product,
        };

        onHandleUpdate(payload);
    };

    const handleOptionSelect = (id: number) => {
        if (custom && id === custom.id) {
            onPendingCustomChange?.(stepIndex, true);
            setPendingCustomFinishId(custom.id);
            handleUpdate({ finishes: [custom.id], custom_finish: '' });
            return;
        }
        onPendingCustomChange?.(stepIndex, false);
        setPendingCustomFinishId(null);
        shouldAutoAdvance.current = true;
        const payload: IProduct = { finishes: [id], custom_finish: '' };
        handleUpdate(payload);
    };

    const clearSearch = () => {
        setSearchQuery('');
    };

    // Filter materials based on search and category
    const filteredData = useMemo(() => {
        let filtered = custom ? [custom, ...data] : data;

        // Filter by search query
        if (searchQuery.trim()) {
            filtered = filtered.filter(item => matchesFinishSearch(item, searchQuery));

            return filtered;
        }

        // Filter out the selected finish from the list
        const restFinishes = filtered.filter(m => m?.id !== displayFinishSelected?.id);

        // Return an array with selected finish first (if exists), then the rest
        const selectedItem = displayFinishSelected ? filtered.find(m => m?.id === displayFinishSelected.id) : null;
        return selectedItem ? [selectedItem, ...restFinishes] : restFinishes;
    }, [searchQuery, displayFinishSelected, data, custom]);

    const otherFinishes = filteredData;

    return (
        <div data-testid="finish-step-shell" className="space-y-4">
            <div>
                <h3 data-testid="finish-step-heading" className="text-lg font-semibold text-slate-900">
                    Surface Finish
                </h3>
                <p className="text-slate-600 text-sm">Define how your part will look and feel</p>
            </div>

            {/* Search Bar */}
            <div className="relative">
                <Search
                    data-testid="finish-search-input"
                    placeholder="Search Surface Finish..."
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    allowClear
                />
            </div>

            {/* Results */}
            {filteredData.length === 0 ? (
                <div data-testid="finish-empty-state" className="text-center py-8">
                    <h4 className="text-sm font-medium text-slate-600 mb-1">No finishes found</h4>
                    {custom && (
                        <span className="flex flex-row items-center cursor-pointer justify-center mb-2">
                            Can't find your Surface Finish? Please select{' '}
                            <Button
                                type="link"
                                onClick={() => {
                                    setSearchQuery('Custom');
                                    if (custom?.id != null) setPendingCustomFinishId(custom.id);
                                }}
                                className="p-1"
                            >
                                Custom
                            </Button>
                        </span>
                    )}

                    <Button
                        type="default"
                        size="small"
                        onClick={() => {
                            setSearchQuery('');
                        }}
                    >
                        Clear all filters
                    </Button>
                </div>
            ) : (
                <div className="space-y-3">
                    {otherFinishes?.length > 0 && (
                        <div>
                            <h4 className="text-sm font-semibold text-slate-700 mb-2">All Surface Finishes</h4>
                            <Spin spinning={loading} indicator={<LoadingOutlined />}>
                                <div
                                    className="grid gap-1 overflow-y-auto pr-2 pl-1"
                                    style={{ maxHeight: 'min(45vh, 380px)' }}
                                >
                                    {otherFinishes?.map(item => (
                                        <StepCard
                                            key={item?.id}
                                            entity="finish"
                                            stepInfo={{ id: item.id, name: item.name }}
                                            selectedId={displayFinishSelected?.id}
                                            onSelect={handleOptionSelect}
                                            customValue={customFinish}
                                            onUpdateCustom={value => {
                                                onPendingCustomChange?.(stepIndex, false);
                                                handleUpdate({ finishes: [custom!.id], custom_finish: value });
                                                setPendingCustomFinishId(null);
                                            }}
                                        />
                                    ))}
                                </div>
                            </Spin>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
